/**
 * Ordered, host-sequenced log of block edits. Every edit gets a global `seq`
 * (contiguous across host changes) and the `term` of the host that ordered it.
 * Two logs agree up to a seq iff their (term, firstSeq) boundaries agree there,
 * which is what `divergenceSeq` uses to find what a peer is missing after a
 * host migration. Conflicting entries (same seq, lower term) are rolled back
 * with their `prev` block id and replaced: higher term always wins.
 *
 * Only a tail is kept in memory (older history lives in the world delta that
 * the snapshot carries); a peer behind the tail start gets a full snapshot.
 */

export interface Edit {
  term: number;
  seq: number;
  x: number; y: number; z: number;
  id: number;
  /** Block id before the edit (for rollback). */
  prev: number;
  /** Identity id of the player whose action produced it ('' = host simulation). */
  by: string;
}

export type EditTuple = [number, number, number, number, number, number, number, string];
export const toTuple = (e: Edit): EditTuple => [e.term, e.seq, e.x, e.y, e.z, e.id, e.prev, e.by];
export function fromTuple(t: unknown): Edit | null {
  if (!Array.isArray(t) || t.length !== 8) return null;
  const [term, seq, x, y, z, id, prev, by] = t as EditTuple;
  for (const v of [term, seq, x, y, z, id, prev]) if (!Number.isInteger(v)) return null;
  if (typeof by !== 'string' || by.length > 32) return null;
  if (y < -64 || y > 512 || id < 0 || id > 0xffff || prev < 0 || prev > 0xffff || seq < 1 || term < 0) return null;
  return { term, seq, x, y, z, id, prev, by };
}

/** [term, firstSeq] pairs, ascending. */
export type Bounds = Array<[number, number]>;

export function termAt(bounds: Bounds, lastSeq: number, seq: number): number {
  if (seq < 1 || seq > lastSeq) return -1;
  let t = -1;
  for (const [term, first] of bounds) {
    if (first <= seq) t = term; else break;
  }
  return t;
}

export class EditLog {
  private entries: Edit[] = [];
  /** seq of entries[0] (when non-empty). */
  private tailStart = 1;
  lastSeq = 0;
  lastTerm = 0;
  bounds: Bounds = [];

  constructor(private readonly maxTail = 8192) {}

  /** Reset to "history up to lastSeq is baked into the world delta". */
  resetTo(lastSeq: number, lastTerm: number, bounds: Bounds): void {
    this.entries = [];
    this.tailStart = lastSeq + 1;
    this.lastSeq = lastSeq;
    this.lastTerm = lastTerm;
    this.bounds = bounds.map((b) => [b[0], b[1]] as [number, number]);
  }

  get firstSeq(): number { return this.tailStart; }
  get size(): number { return this.entries.length; }

  entryAt(seq: number): Edit | undefined {
    const i = seq - this.tailStart;
    return i >= 0 && i < this.entries.length ? this.entries[i] : undefined;
  }

  /** Entries with seq >= from that are still in the tail. */
  since(from: number): Edit[] {
    const i = Math.max(0, from - this.tailStart);
    return this.entries.slice(i);
  }

  /** Can this log serve every entry from `from` on? */
  covers(from: number): boolean { return from >= this.tailStart || from > this.lastSeq; }

  /** Host side: order a new edit. */
  next(term: number, x: number, y: number, z: number, id: number, prev: number, by: string): Edit {
    const e: Edit = { term, seq: this.lastSeq + 1, x, y, z, id, prev, by };
    this.append(e);
    return e;
  }

  append(e: Edit): void {
    if (e.seq !== this.lastSeq + 1) throw new Error(`edit log gap: have ${this.lastSeq}, got ${e.seq}`);
    if (this.entries.length === 0) this.tailStart = e.seq;
    this.entries.push(e);
    if (this.bounds.length === 0 || this.bounds[this.bounds.length - 1][0] !== e.term) this.bounds.push([e.term, e.seq]);
    this.lastSeq = e.seq;
    this.lastTerm = e.term;
    if (this.entries.length > this.maxTail) {
      const drop = this.entries.length - this.maxTail;
      this.entries.splice(0, drop);
      this.tailStart += drop;
    }
  }

  /** Remove entries with seq >= from; returns them newest first (for undo). */
  private truncate(from: number): Edit[] {
    const i = Math.max(0, from - this.tailStart);
    const cut = this.entries.splice(i).reverse();
    this.lastSeq = from - 1;
    while (this.bounds.length && this.bounds[this.bounds.length - 1][1] > this.lastSeq) this.bounds.pop();
    const last = this.entryAt(this.lastSeq);
    this.lastTerm = last ? last.term : (this.bounds.length ? this.bounds[this.bounds.length - 1][0] : 0);
    if (this.entries.length === 0) this.tailStart = from;
    return cut;
  }

  /**
   * Merge entries ordered by the authoritative host. Returns the block writes
   * the caller must perform: `undo` (set x,y,z back to prev, in order) and then
   * `apply` (set x,y,z to id, in order). `gap` is the first missing seq when
   * the batch skips ahead (caller requests a resend from there).
   */
  mergeAuthoritative(batch: Edit[]): { undo: Edit[]; apply: Edit[]; gap: number } {
    const undo: Edit[] = [];
    const apply: Edit[] = [];
    const sorted = [...batch].sort((a, b) => a.seq - b.seq);
    for (const e of sorted) {
      if (e.seq <= this.lastSeq) {
        const mine = this.entryAt(e.seq);
        if (!mine) continue; // older than our tail: already baked in
        if (mine.term === e.term) continue; // duplicate
        if (mine.term > e.term) continue; // we already have a newer ordering
        undo.push(...this.truncate(e.seq));
      }
      if (e.seq > this.lastSeq + 1) return { undo, apply, gap: this.lastSeq + 1 };
      this.append(e);
      apply.push(e);
    }
    return { undo, apply, gap: 0 };
  }
}

/**
 * First seq at which a peer's log (given by its bounds + lastSeq) may differ
 * from the host's. The host resends everything from there.
 */
export function divergenceSeq(host: { bounds: Bounds; lastSeq: number }, peerBounds: Bounds, peerLastSeq: number): number {
  const limit = Math.min(host.lastSeq, peerLastSeq);
  const points = new Set<number>([1]);
  for (const [, s] of host.bounds) points.add(s);
  for (const [, s] of peerBounds) points.add(s);
  const sorted = [...points].filter((p) => p <= limit).sort((a, b) => a - b);
  for (const p of sorted) {
    if (termAt(host.bounds, host.lastSeq, p) !== termAt(peerBounds, peerLastSeq, p)) return p;
  }
  return limit + 1;
}
